import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import { TPromoBannerBase } from '@onesource/schemas';
import { sanitizeCustomHtml } from '@onesource/utils';
import { FC, useEffect, useRef } from 'react';

export interface PromoBannerCustomProps {
  /** Only `customHtml` is read — the form's live preview passes a bare `{ customHtml }`. */
  banner: Pick<TPromoBannerBase, 'customHtml'>;
  sx?: SxProps<Theme>;
}

/**
 * Custom promo banner layout (PromoBannerType.CUSTOM) — renders admin-authored
 * HTML as-is so the design is entirely up to the admin.
 *
 * The markup runs through the shared DOMPurify allow-list (scripts, `on*`
 * handlers and `javascript:` URLs are dropped; `sanitizeCustomHtml` keeps a
 * leading `<style>` block) and is mounted inside an open shadow root rather
 * than via `dangerouslySetInnerHTML`. The shadow boundary
 * scopes any `<style>` block the admin ships to the banner: their selectors
 * can't restyle the surrounding page, and the app's global CSS can't restyle
 * the banner. Inherited properties (font-family, color) still flow in from the
 * host, so unstyled text picks up the app typography.
 */
export const PromoBannerCustom: FC<PromoBannerCustomProps> = ({ banner, sx }) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const html = banner.customHtml ?? '';

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }
    // `attachShadow` throws on a host that already has one (StrictMode
    // re-runs effects), so reuse the existing root.
    const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    root.innerHTML = sanitizeCustomHtml(html);
  }, [html]);

  return <Box ref={hostRef} sx={{ width: '100%', minWidth: 0, ...sx }} />;
};
